// src/Pages/BlitzPay/t2a/PayRequestSuccessPage.js
//
// Lands here after ANY public pay-request-by-token payment completes —
// this is the actual success_url Stripe/PayPal redirect the buyer to for
// POST /gu-balance/pay-request/:token (see PaymentCTA.js / PayRequestPage.js).
// Shared infra, not Commerce-specific — but Commerce-aware: if
// ProductCheckoutPage.js cached checkout context in sessionStorage right
// before redirecting to Stripe/PayPal (keyed by this same token), this page
// reads it back and gives the buyer one of two experiences:
//   1. A configured return_url (or the referrer captured when checkout
//      started) → auto-redirect back to the merchant's own site, with a
//      manual "continue now" button in case auto-redirect is blocked.
//   2. No return_url → a branded "Thanks for purchasing! {company} will be
//      back to you shortly" confirmation.
// No cached context at all (e.g. a regular BlitzPay invoice paid through
// this same public flow, not a Commerce checkout) → falls back to a
// generic payment-received confirmation. Purely a UI landing spot — actual
// payment confirmation already happened server-side via webhook, this page
// doesn't re-verify anything.

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Paper, Stack, CircularProgress, Button } from "@mui/material";
import { CheckCircle as CheckCircleIcon, Lock as LockIcon } from "@mui/icons-material";
import { alpha, keyframes } from "@mui/material/styles";

const BLITZ = {
  sky: "#0ea5e9", skyDark: "#0284c7", skyDeep: "#075985", skyLight: "#7dd3fc", ink: "#0f172a",
};

const fadeUp = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;

const AUTO_REDIRECT_SECONDS = 4;

function PageShell({ children }) {
  return (
    <Box sx={{
      minHeight: "100vh", position: "relative", overflow: "hidden",
      background: `linear-gradient(160deg, ${BLITZ.skyDeep} 0%, ${BLITZ.sky} 55%, ${alpha(BLITZ.skyLight, 0.9)} 100%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      py: { xs: 4, md: 8 }, px: 2,
    }}>
      <Box sx={{ width: "100%", maxWidth: 440, position: "relative", zIndex: 1, animation: `${fadeIn} 0.3s ease` }}>
        {children}
      </Box>
    </Box>
  );
}

export default function PayRequestSuccessPage() {
  const { token } = useParams();
  // undefined = haven't checked sessionStorage yet, null = checked and there's no cached context.
  const [context, setContext] = useState(undefined);

  useEffect(() => {
    const key = `commerce_checkout_${token}`;
    try {
      const raw = sessionStorage.getItem(key);
      sessionStorage.removeItem(key);
      setContext(raw ? JSON.parse(raw) : null);
    } catch {
      setContext(null);
    }
  }, [token]);

  const returnTarget = context?.return_url || context?.referrer || null;
  const [secondsLeft, setSecondsLeft] = useState(AUTO_REDIRECT_SECONDS);

  useEffect(() => {
    if (!returnTarget) return undefined;
    if (secondsLeft <= 0) {
      window.location.href = returnTarget;
      return undefined;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [returnTarget, secondsLeft]);

  if (context === undefined) {
    return (
      <PageShell>
        <Paper elevation={0} sx={{ borderRadius: 4, p: 4, textAlign: "center" }}>
          <CircularProgress size={28} sx={{ color: BLITZ.sky }} />
        </Paper>
      </PageShell>
    );
  }

  const companyName = context?.company_name;

  return (
    <PageShell>
      <Paper elevation={0} sx={{ borderRadius: 4, p: { xs: 3, md: 4 }, textAlign: "center", animation: `${fadeUp} 0.25s ease` }}>
        <Box sx={{
          width: 72, height: 72, borderRadius: "50%", bgcolor: "#dcfce7", color: "#16a34a",
          display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2.5,
        }}>
          <CheckCircleIcon sx={{ fontSize: 40 }} />
        </Box>

        <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
          Payment received
        </Typography>

        {returnTarget ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Thanks for your purchase{companyName ? ` from ${companyName}` : ""}! Redirecting you back
              {companyName ? ` to ${companyName}` : ""} in {secondsLeft}s…
            </Typography>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={() => { window.location.href = returnTarget; }}
              sx={{ textTransform: "none", bgcolor: BLITZ.sky, "&:hover": { bgcolor: BLITZ.skyDark } }}
            >
              Continue{companyName ? ` to ${companyName}` : ""} now →
            </Button>
          </>
        ) : companyName ? (
          <Typography variant="body2" color="text.secondary">
            Thanks for purchasing! {companyName} will be back to you shortly.
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Your payment was completed successfully. You can close this window.
          </Typography>
        )}
      </Paper>

      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} sx={{ mt: 2.5, opacity: 0.85 }}>
        <LockIcon sx={{ fontSize: 13, color: "white" }} />
        <Typography variant="caption" sx={{ color: "white", fontSize: 11 }}>
          Secured by Blitz
        </Typography>
      </Stack>
    </PageShell>
  );
}
